import { useState, useEffect, useRef } from 'react';
import { useCMS } from '../../context/CMSContext';
import { Play } from 'lucide-react';

const InteractiveVideo = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [rotation, setRotation] = useState({ x: 0, y: 0 });
    const [offset, setOffset] = useState(0); // For parallax
    const [isHovering, setIsHovering] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const iframeRef = useRef(null);

    // Get YouTube Video ID from CMS Context
    const { content } = useCMS();
    const YOUTUBE_VIDEO_ID = content?.youtubeVideoId || "7K8A8Zxp1mg"; // Fallback ID
    
    // Construct the embed URL with parameters to hide all controls, branding and enable JS API
    const youtubeUrl = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?enablejsapi=1&rel=0&modestbranding=1&controls=0&showinfo=0&disablekb=1&iv_load_policy=3&fs=0`;
    const thumbnailUrl = `https://img.youtube.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`;

    // 1. Intersection Observer for Scroll-Triggered Entry
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.15 }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // 2. Parallax Effect on Scroll (Movement during scroll)
    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            // Calculate how far the component is from the center of the screen
            const centerOffset = (rect.top + rect.height / 2) - viewportHeight / 2;
            // Apply a subtle parallax movement
            setOffset(centerOffset * 0.05);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 3. 3D Tilt Effect Calculation
    const handleMouseMove = (e) => {
        if (!videoRef.current) return;

        const rect = videoRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const mouseX = e.clientX - centerX;
        const mouseY = e.clientY - centerY;

        // Map mouse position to rotation (max 10 degrees for more "flexible" feel)
        const rotateX = (mouseY / (rect.height / 2)) * -10;
        const rotateY = (mouseX / (rect.width / 2)) * 10;

        setRotation({ x: rotateX, y: rotateY });
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setRotation({ x: 0, y: 0 });
        setIsHovering(false);
    };

    const togglePlay = () => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) return;
        const func = isPlaying ? 'pauseVideo' : 'playVideo';
        iframeRef.current.contentWindow.postMessage(`{"event":"command","func":"${func}","args":""}`, '*');
        setIsPlaying(!isPlaying);
    };

    const transformStyle = {
        transform: `perspective(1200px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) translateY(${offset}px) scale3d(${isHovering ? 1.05 : 1}, ${isHovering ? 1.05 : 1}, ${isHovering ? 1.05 : 1})`,
        // Enhanced transition for fluidity (no snapping)
        transition: 'transform 0.4s cubic-bezier(0.1, 0.4, 0.2, 1)',
        willChange: 'transform',
    };

    return (
        <div
            ref={containerRef}
            className={`relative w-full max-w-4xl mx-auto mt-10 sm:mt-16 mb-16 sm:mb-24 z-10 px-3 sm:px-4 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-24'
                }`}
        >
            {/* --- Premium Video Container with Organic Movement --- */}
            <div
                className="relative group"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                {/* تأثير التوهج الخلفي السائل (Liquid Glow Effect) */}
                <div className="absolute -inset-2 bg-gradient-to-r from-primary-600/30 to-amber-600/30 dark:from-primary-500/20 dark:to-purple-500/20 rounded-[2.5rem] blur-2xl opacity-40 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"></div>

                {/* حاوية الفيديو الرئيسية */}
                <div
                    ref={videoRef}
                    style={transformStyle}
                    className="relative aspect-video bg-transparent dark:bg-transparent rounded-2xl sm:rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.3)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.6)] border-[4px] sm:border-[6px] border-white dark:border-zinc-800/80 ring-1 ring-black/5 dark:ring-white/10 group cursor-pointer flex items-center justify-center"
                >
                    {/* The Invisible click catcher & Custom Overlay */}
                    <div 
                        className="absolute inset-0 z-20 cursor-pointer overflow-hidden flex items-center justify-center group/overlay"
                        onClick={togglePlay}
                    >
                        {/* Thumbnail Cover - shown only when paused */}
                        <div 
                            className={`absolute inset-0 bg-slate-900 transition-opacity duration-300 ${isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                        >
                            {/* Fallback to background color if thumbnail cannot load */}
                            <img src={thumbnailUrl} alt="Thumbnail" className="w-full h-full object-cover opacity-80 group-hover/overlay:opacity-60 transition-opacity duration-500" />
                            <div className="absolute inset-0 bg-black/20"></div>
                        </div>

                        {/* Custom Play Button */}
                        <div className={`absolute z-30 w-20 h-20 bg-primary-600 text-white rounded-full flex items-center justify-center backdrop-blur-md shadow-[0_0_40px_rgba(230,0,35,0.6)] transform transition-all duration-300 ${isPlaying ? 'scale-0 opacity-0' : 'scale-100 opacity-100 group-hover/overlay:scale-110'}`}>
                            <Play className="w-10 h-10 ml-1 fill-white" />
                        </div>
                    </div>

                    <iframe
                        ref={iframeRef}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 pointer-events-none z-10"
                        src={youtubeUrl}
                        title="فيديو تعريفي لمدرسة لباب"
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                    ></iframe>
                </div>
            </div>

        </div>
    );
};

export default InteractiveVideo;
